/**
 * Parse ##MDA ambient packages from the mud (web clients only).
 * Packages may appear mid-stream (same TCP chunk as other text).
 */

export interface AmbientState {
  indoors: boolean;
  sector: string;
  sky: string;
  sun: string;
  safe: boolean;
  boat: boolean;
  dark: number;
  combat: boolean;
}

export const defaultAmbient = (): AmbientState => ({
  indoors: false,
  sector: "unknown",
  sky: "unknown",
  sun: "unknown",
  safe: false,
  boat: false,
  dark: 0,
  combat: false,
});

/** Match ##MDA { ... } even when glued to previous text; JSON is flat (no nested {}). */
const MDA_RE = /##MDA\s*(\{[^\r\n]*\})/g;

function parsePackage(json: string): { ambient: AmbientState | null; hello: boolean } {
  try {
    const obj = JSON.parse(json) as Record<string, unknown>;
    if (obj.t === "hello") return { ambient: null, hello: true };
    if (obj.t === "ambient") {
      return {
        hello: false,
        ambient: {
          indoors: obj.in === 1 || obj.in === true,
          sector: typeof obj.sect === "string" ? obj.sect : "unknown",
          sky: typeof obj.sky === "string" ? obj.sky : "unknown",
          sun: typeof obj.sun === "string" ? obj.sun : "unknown",
          safe: obj.safe === 1 || obj.safe === true,
          boat: obj.boat === 1 || obj.boat === true,
          dark: typeof obj.dark === "number" ? obj.dark : Number(obj.dark) || 0,
          combat: obj.combat === 1 || obj.combat === true,
        },
      };
    }
  } catch {
    /* ignore */
  }
  return { ambient: null, hello: false };
}

/**
 * Split raw mud text into displayable text and last ambient payload.
 * Removes all ##MDA packages from scrollback.
 */
export function extractAmbient(raw: string): {
  display: string;
  ambient: AmbientState | null;
  hello: boolean;
} {
  let ambient: AmbientState | null = null;
  let hello = false;

  const display = raw.replace(MDA_RE, (_full, json: string) => {
    const parsed = parsePackage(json);
    if (parsed.hello) hello = true;
    if (parsed.ambient) ambient = parsed.ambient;
    return ""; // strip from display
  });

  return { display, ambient, hello };
}

export function ambientCssClasses(a: AmbientState): string[] {
  const classes: string[] = ["has-ambient"];

  if (a.indoors) classes.push("amb-indoors");
  else classes.push("amb-outdoors");

  if (a.boat) classes.push("amb-boat");
  if (a.safe) classes.push("amb-safe");
  if (a.combat) classes.push("amb-combat");

  if (a.dark >= 3) classes.push("amb-darkest");
  else if (a.dark === 2) classes.push("amb-darker");
  else if (a.dark === 1) classes.push("amb-dark");

  switch (a.sky) {
    case "cloudless":
      classes.push("amb-sky-clear");
      break;
    case "cloudy":
      classes.push("amb-sky-cloudy");
      break;
    case "raining":
      classes.push("amb-sky-rain");
      break;
    case "lightning":
      classes.push("amb-sky-storm");
      break;
  }

  switch (a.sun) {
    case "dark":
      classes.push("amb-night");
      break;
    case "rise":
      classes.push("amb-sunrise");
      break;
    case "light":
      classes.push("amb-day");
      break;
    case "set":
      classes.push("amb-sunset");
      break;
  }

  if (a.sector && a.sector !== "unknown") {
    classes.push(`amb-sect-${a.sector.replace(/[^a-z0-9_]/gi, "_")}`);
  }

  return classes;
}

export function ambientLabel(a: AmbientState): string {
  const bits: string[] = [];
  if (a.indoors) bits.push("indoors");
  else bits.push("outdoors");
  if (a.sector && a.sector !== "unknown") bits.push(a.sector.replace(/_/g, " "));
  if (!a.indoors && a.sky !== "unknown") bits.push(a.sky);
  if (a.sun !== "unknown") bits.push(a.sun === "dark" ? "night" : a.sun);
  if (a.boat) bits.push("boat");
  if (a.safe) bits.push("safe");
  if (a.combat) bits.push("combat");
  if (a.dark > 0) bits.push(a.dark >= 3 ? "pitch dark" : "dark");
  return bits.join(" · ");
}

export function applyAmbientToDom(a: AmbientState | null): void {
  const root = document.documentElement;
  const remove: string[] = [];
  root.classList.forEach((c) => {
    if (c.startsWith("amb-") || c === "has-ambient") remove.push(c);
  });
  remove.forEach((c) => root.classList.remove(c));

  if (!a) {
    root.removeAttribute("data-sector");
    root.removeAttribute("data-sky");
    root.removeAttribute("data-sun");
    return;
  }
  ambientCssClasses(a).forEach((c) => root.classList.add(c));
  root.dataset.sector = a.sector;
  root.dataset.sky = a.sky;
  root.dataset.sun = a.sun;
}
